import { Box, Stack, Skeleton } from "@mui/material";

// ─── Skeleton for SectionHeader ───────────────────────────────────────────────
function SectionHeaderSkeleton() {
	return (
		<Stack direction="row" alignItems="center" spacing={1.5} mb={2} px={{ xs: 1, sm: 2 }}>
			<Skeleton variant="circular" width={24} height={24} />
			<Skeleton variant="text" width={160} height={28} sx={{ borderRadius: 1 }} />
		</Stack>
	);
}

// ─── Single stat card skeleton ────────────────────────────────────────────────
function StatCardSkeleton({ sx }) {
	return (
		<Box
			sx={{
				flex: "1 1 0",
				width: "100%",
				minHeight: { xs: 80, sm: 100 },
				display: "flex",
				alignItems: "center",
				gap: { xs: 1.5, sm: 2 },
				backgroundColor: "#fff",
				borderRadius: 2,
				p: { xs: "1rem 1.125rem", sm: "1.25rem 1.5rem" },
				borderRight: "4px solid #E0E7F0",
				boxShadow: "0 2px 8px rgba(0,0,0,0.06), 0 0 1px rgba(0,0,0,0.04)",
				...sx,
			}}
		>
			{/* Icon box */}
			<Skeleton
				variant="rounded"
				sx={{
					width: { xs: 40, sm: 48 },
					height: { xs: 40, sm: 48 },
					borderRadius: 2,
					flexShrink: 0,
				}}
			/>
			{/* Text block */}
			<Box sx={{ minWidth: 0, flex: 1 }}>
				<Skeleton variant="text" width="55%" height={16} sx={{ mb: 0.5 }} />
				<Skeleton variant="text" width="70%" height={36} sx={{ borderRadius: 1 }} />
			</Box>
		</Box>
	);
}

// ─── Qualification status card skeleton ───────────────────────────────────────
function QualificationStatusCardSkeleton() {
	return (
		<Box
			sx={{
				width: "100%",
				minHeight: { xs: 80, sm: 100 },
				display: "flex",
				alignItems: "center",
				gap: { xs: 1.5, sm: 2 },
				backgroundColor: "#fff",
				borderRadius: 2,
				p: { xs: "1rem 1.125rem", sm: "1.25rem 1.5rem" },
				borderRight: "4px solid #E0E7F0",
				boxShadow: "0 2px 8px rgba(0,0,0,0.06), 0 0 1px rgba(0,0,0,0.04)",
			}}
		>
			{/* Icon */}
			<Skeleton variant="rounded" sx={{ width: { xs: 40, sm: 48 }, height: { xs: 40, sm: 48 }, borderRadius: 2, flexShrink: 0 }} />

			{/* Two badge columns */}
			<Stack direction="row" gap={2} alignItems="flex-start">
				<Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
					<Skeleton variant="text" width={55} height={14} />
					<Skeleton variant="rounded" width={90} height={28} sx={{ borderRadius: 99 }} />
				</Box>
				<Box sx={{ width: 1, alignSelf: "stretch", backgroundColor: "#F0F4F8", mx: 0.5 }} />
				<Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
					<Skeleton variant="text" width={80} height={14} />
					<Skeleton variant="rounded" width={100} height={28} sx={{ borderRadius: 99 }} />
				</Box>
			</Stack>
		</Box>
	);
}

// ─── Network book grid skeleton (4 stacked stat cards) ───────────────────────
function NetworkBookSkeleton() {
	return (
		<Box
			sx={{
				display: "grid",
				gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr", md: "1fr" },
				gap: 2,
				width: "100%",
			}}
		>
			{[0, 1, 2, 3].map((i) => (
				<StatCardSkeleton key={i} />
			))}
		</Box>
	);
}

// ─── Payment summary / checkout box skeleton ──────────────────────────────────
function TotalCheckoutSkeleton() {
	return (
		<Box flex={{ xs: 1, lg: 1 }} width="100%">
			<SectionHeaderSkeleton />

			<Box
				sx={{
					backgroundColor: "#fff",
					borderRadius: 2,
					boxShadow: "0 2px 12px rgba(0,0,0,0.07), 0 0 1px rgba(0,0,0,0.04)",
					overflow: "hidden",
					border: "1px solid #EEF2F7",
				}}
			>
				<Box sx={{ px: 2.5, pt: 2, pb: 2 }}>
					{/* "Summary" label + month picker row */}
					<Skeleton variant="text" width={100} height={22} sx={{ mb: 1 }} />
					<Stack direction="row" alignItems="center" gap={2} my={3.5} ml={1.5}>
						<Skeleton variant="text" width={90} height={28} />
						<Skeleton variant="rounded" width={140} height={36} sx={{ borderRadius: 1.5 }} />
					</Stack>

					{/* Line item 1 — Personal Book Service Fees */}
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 2,
							borderRadius: 2,
							borderLeft: "4px solid #E0E7F0",
							boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
							backgroundColor: "#FEFCF9",
							p: { xs: 1.5, sm: 2 },
							mb: 2,
						}}
					>
						<Skeleton
							variant="rounded"
							sx={{ width: { xs: 44, sm: 64 }, height: { xs: 44, sm: 64 }, borderRadius: 1.5, flexShrink: 0 }}
						/>
						<Box sx={{ flex: 1 }}>
							<Skeleton variant="text" width="50%" height={18} sx={{ mb: 0.5 }} />
							<Skeleton variant="text" width="80%" height={14} />
						</Box>
						<Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.75, flexShrink: 0 }}>
							<Skeleton variant="text" width={60} height={12} />
							<Skeleton variant="text" width={80} height={32} />
						</Box>
					</Box>

					<Box sx={{ height: 1, backgroundColor: "#F0F4F8", mb: 2 }} />

					{/* Line item 2 — Network Residual Income */}
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 2,
							borderRadius: 2,
							borderLeft: "4px solid #E0E7F0",
							boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
							backgroundColor: "#FAFDFA",
							p: { xs: 1.5, sm: 2 },
						}}
					>
						<Skeleton
							variant="rounded"
							sx={{ width: { xs: 44, sm: 64 }, height: { xs: 44, sm: 64 }, borderRadius: 1.5, flexShrink: 0 }}
						/>
						<Box sx={{ flex: 1 }}>
							<Skeleton variant="text" width="55%" height={18} sx={{ mb: 0.5 }} />
							<Skeleton variant="text" width="75%" height={14} />
						</Box>
						<Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.75, flexShrink: 0 }}>
							<Skeleton variant="text" width={60} height={12} />
							<Skeleton variant="text" width={80} height={32} />
							<Skeleton variant="rounded" width={110} height={24} sx={{ borderRadius: 1 }} />
						</Box>
					</Box>
				</Box>

				{/* Dark footer row */}
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						backgroundColor: "#1D54A2",
						px: 2.5,
						py: 2,
					}}
				>
					<Stack direction="row" alignItems="center" gap={2}>
						<Skeleton variant="rounded" width={67} height={67} sx={{ borderRadius: 1.5, bgcolor: "rgba(255,255,255,0.15)" }} />
						<Box>
							<Skeleton variant="text" width={130} height={18} sx={{ bgcolor: "rgba(255,255,255,0.2)", mb: 0.5 }} />
							<Skeleton variant="text" width={190} height={14} sx={{ bgcolor: "rgba(255,255,255,0.15)" }} />
						</Box>
					</Stack>
					<Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.75 }}>
						<Skeleton variant="text" width={70} height={14} sx={{ bgcolor: "rgba(255,255,255,0.2)" }} />
						<Skeleton variant="rounded" width={120} height={38} sx={{ borderRadius: 1.5, bgcolor: "rgba(255,255,255,0.2)" }} />
						<Skeleton variant="rounded" width={100} height={24} sx={{ borderRadius: 99, bgcolor: "rgba(255,255,255,0.15)" }} />
					</Box>
				</Box>
			</Box>
		</Box>
	);
}

// ─── Full dashboard skeleton ──────────────────────────────────────────────────
export default function DashboardSkeleton() {
	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 3, mb: 4, width: "100%" }}>
			{/* ROW 1: Status + Personal Book + Leadership Bonuses */}
			<Stack direction={{ xs: "column", lg: "row" }} gap={{ xs: 3, lg: 5 }} mb={{ xs: 0, sm: 3 }}>
				{/* Qualification Status */}
				<Box flex={0.8} width="100%">
					<SectionHeaderSkeleton />
					<QualificationStatusCardSkeleton />
				</Box>

				{/* Personal Book */}
				<Box flex={1} width="100%">
					<SectionHeaderSkeleton />
					<StatCardSkeleton
						sx={{
							flexBasis: { xs: "100%", sm: "calc(50% - 8px)", md: 0 },
							flexGrow: 1,
						}}
					/>
				</Box>

				{/* Leadership Bonuses */}
				<Box flex={{ xs: 1, lg: 0.8 }} width="100%">
					<SectionHeaderSkeleton />
					<StatCardSkeleton />
				</Box>
			</Stack>

			{/* ROW 2: Network Book + Payment Summary */}
			<Stack direction={{ xs: "column", lg: "row" }} gap={{ xs: 3, lg: 5 }}>
				{/* Network Book */}
				<Box flex={1} width="100%">
					<SectionHeaderSkeleton />
					<NetworkBookSkeleton />
				</Box>

				{/* Payment Summary */}
				<TotalCheckoutSkeleton />
			</Stack>
		</Box>
	);
}
